import { Link } from 'react-router-dom';

import { HighlightedSelections } from '@/components/HighlightedSelections';
import { OutletLogo } from '@/components/feed/FeedCard';
import { useLiveMentions } from '@/hooks/useLiveMentions';
import { cn } from '@/services/utils';
import type { FeedItem } from '@/services';
import { timeAgo } from '@/utils/timeAgo';

export function LiveMentionsWidget() {
  const { items, loading } = useLiveMentions();

  return (
    <div className="rounded-2xl border border-[#ece9e2] bg-white px-[18px] pb-2 pt-4 font-[family-name:var(--font-meaning-sans)]">
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-2">
          <span className="relative flex size-2 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-[#e0362f] opacity-75" />
            <span className="relative inline-flex size-2 rounded-full bg-[#e0362f]" />
          </span>
          <div className="text-lg font-extrabold text-[#1e1c18]">Live mentions</div>
        </div>
      </div>
      <div className="mt-1 text-sm leading-[1.45] text-[#7b7669]">Your words spotted online recently</div>

      <div className="mt-2">
        {loading && items.length === 0 && (
          <div className="py-4 text-sm text-[#a39c8d]">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="py-4 text-sm text-[#a39c8d]">No mentions yet.</div>
        )}
        {items.map((item, index) => (
          <LiveMentionRow key={item.matchId} item={item} isLast={index === items.length - 1} />
        ))}
      </div>
    </div>
  );
}

interface LiveMentionRowProps {
  item: FeedItem;
  isLast: boolean;
}

function LiveMentionRow({ item, isLast }: LiveMentionRowProps) {
  const highlights = (item.matchedTokens ?? [])
    .map((span) => ({ start: span.start, length: span.end - span.start }))
    .sort((left, right) => left.start - right.start);
  const outletName = item.siteName ?? item.articleDomain;

  return (
    <div className={cn('py-[11px]', !isLast && 'border-b border-[#f4f1ec]')}>
      <div className="flex items-center gap-[7px] text-[13px] text-[#7b7669]">
        <OutletLogo item={item} tiny />
        <span className="truncate font-bold text-[#57534a]">{outletName}</span>
      </div>
      <div className="mt-[5px] rounded-r-sm border-l-2 border-[#f0c066] pl-[9px] font-display text-[15px] leading-[1.45] text-[#3d3a33]">
        <HighlightedSelections text={item.sentence} selections={highlights} />
      </div>
      <div className="mt-[5px] flex items-baseline justify-between gap-3 text-[13px] text-[#7b7669]">
        <div className="min-w-0 truncate">
          for{' '}
          <Link to={`/meaning/card/${item.meaningId}`} className="font-semibold text-[#2f5c4e] hover:underline">
            {item.phraseText}
          </Link>
        </div>
        {item.publishedAtUtc && <span className="shrink-0 whitespace-nowrap">{timeAgo(item.publishedAtUtc)}</span>}
      </div>
    </div>
  );
}
